import React, { FC } from 'react';

import {
  FullPaymentType,
  InstallmentPaymentType,
  PriceSectionType,
} from './types';
import styles from './styles.module.css';

const PRICE_PARTS_DEFAULTS = {
  symbol: '$',
  amount: '--',
  decimals: '--',
};

function isInstallmentPaymentType(
  payment: FullPaymentType | InstallmentPaymentType
): payment is InstallmentPaymentType {
  return !['full_pay', 'in_full'].includes(payment.frequency);
}

function isFullPaymentType(
  payment: FullPaymentType | InstallmentPaymentType
): payment is FullPaymentType {
  return ['full_pay', 'in_full'].includes(payment.frequency);
}

export const FlexiblePriceSummary: FC<PriceSectionType> = props => {
  const splitPriceParts = (price: number = 0) => {
    const symbol = '$';
    const formattedValue = price.toLocaleString('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
    const [amount, decimals] = formattedValue.split('.');

    return {
      symbol: symbol ? symbol : PRICE_PARTS_DEFAULTS.symbol,
      amount: price ? amount : PRICE_PARTS_DEFAULTS.amount,
      decimals: price ? decimals : PRICE_PARTS_DEFAULTS.decimals,
    };
  };

  const renderPriceWithFrequency = (price: number, frequency: string) => {
    const { symbol, amount, decimals } = splitPriceParts(price);

    return (
      <div className={styles.price}>
        <span className={styles.priceSymbol}>{symbol}</span>
        <span className={styles.priceAmount}>{amount}</span>
        <p className={styles.priceDetails}>
          <span className={styles.priceDecimal}>.{decimals}</span>
          <span className={styles.priceFrequency}>/{frequency}</span>
        </p>
      </div>
    );
  };

  const renderInstallmentPaymentDetails = (
    downPayment: number,
    totalAmount: number
  ) => {
    const {
      symbol: downPaymentSymbol,
      amount: downPaymentAmount,
      decimals: downPaymentDecimals,
    } = splitPriceParts(downPayment);
    const {
      symbol: totalAmountSymbol,
      amount: totalAmountAmount,
      decimals: totalAmountDecimals,
    } = splitPriceParts(totalAmount);

    return (
      <div className={styles.installmentDetails}>
        <p className={styles.installmentDetailsDownPayment}>
          +{' '}
          <span>{`${downPaymentSymbol}${downPaymentAmount}.${downPaymentDecimals}`}</span>{' '}
          down payment
        </p>
        <p className={styles.installmentDetailsTotal}>
          Total premium:{' '}
          <span>{`${totalAmountSymbol}${totalAmountAmount}.${totalAmountDecimals}`}</span>
        </p>
      </div>
    );
  };

  const renderFullPaymentDetails = (totalPremiumAmount: number = 0) => {
    const {
      symbol: totalAmountSymbol,
      amount: totalAmountAmount,
      decimals: totalAmountDecimals,
    } = splitPriceParts(totalPremiumAmount);

    return (
      <div className={styles.fullPaymentDetails}>
        <p className={styles.infoLabel}>+ no additional payments</p>
        <p className={styles.totalAmount}>
          Total premium:{' '}
          <span>{`${totalAmountSymbol}${totalAmountAmount}.${totalAmountDecimals}`}</span>
        </p>
      </div>
    );
  };

  if (isInstallmentPaymentType(props)) {
    const {
      downPayment,
      totalAmount,
      installmentAmount,
      frequencyLabel,
    } = props;

    return (
      <div className={styles.flexiblePriceSummary}>
        <h3 className={styles.sectionHeader}>Here is our final quote</h3>
        <div className={styles.priceSection}>
          {renderPriceWithFrequency(installmentAmount, frequencyLabel)}
          {renderInstallmentPaymentDetails(downPayment, totalAmount)}
        </div>
      </div>
    );
  }

  if (isFullPaymentType(props)) {
    const { dueToday, totalAmount } = props;

    return (
      <div className={styles.flexiblePriceSummary}>
        <h3 className={styles.sectionHeader}>Here is our final quote</h3>
        <div className={styles.priceSection}>
          {renderPriceWithFrequency(dueToday, 'due today')}
          {renderFullPaymentDetails(totalAmount)}
        </div>
      </div>
    );
  }

  return null;
};
